import { Download, Keyboard, Lock, MonitorSmartphone, Printer, Tag } from 'lucide-react'

import { Fragment } from 'react'
import { NavLink } from 'react-router-dom'

import { useRegisterContext } from '@/contexts/RegisterContext'
import { useSession } from '@/contexts/SessionContext'
import { cn } from '@/lib/utils'
import { openCustomerDisplay } from '@/services/customerDisplay'
import { useInstallPrompt } from '@/hooks/ui/useInstallPrompt'

import { SECTION_LABEL, visibleNav } from './nav'
import { SyncStatus } from './SyncStatus'
import { initials } from '@/utils'


const item =
  'flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-md text-micro font-medium text-sidebar-muted outline-none transition-colors hover:bg-sidebar-hover hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring'

/** Sidebar in its icon-rail form (as the admin's collapsed sidebar), with labels kept for new cashiers. */
export const PosRail = ({
  onShowShortcuts,
  onShowPrinter,
  onShowLabels,
}: {
  onShowShortcuts: () => void
  onShowPrinter: () => void
  onShowLabels: () => void
}) => {
  const register = useRegisterContext()
  const { stage, lock, can } = useSession()
  const { install } = useInstallPrompt()
  const nav = visibleNav(stage, can).filter((n) => !n.hideInRail)
  return (
    <nav className="flex h-16 shrink-0 items-stretch gap-1 overflow-x-auto px-2 py-1 scrollbar-hide md:h-auto md:w-[72px] md:flex-col md:overflow-y-auto md:overflow-x-hidden md:px-1.5 md:py-2">
      <img src="/favicon.png" alt="IWS" className="mx-auto mb-3 hidden size-8 shrink-0 rounded-md md:block" />
      {/* Sell, branch office, stock room (§11.1); the whole rail scrolls, Sync and Lock included (as on Android). */}
      <div className="flex min-w-max flex-1 items-stretch gap-1 md:min-w-0 md:flex-none md:flex-col">
        {nav.map(({ to, label, icon: Icon, section }, i) => (
          <Fragment key={to}>
            {i > 0 && nav[i - 1].section !== section && (
              <div
                role="separator"
                aria-label={SECTION_LABEL[section]}
                title={SECTION_LABEL[section]}
                className="mx-2 my-1 hidden h-px shrink-0 bg-sidebar-hover md:block"
              />
            )}
            <NavLink
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(item, 'min-w-14 flex-1 shrink-0 md:h-14 md:flex-none', isActive && 'bg-sidebar-accent text-sidebar-accent-foreground')
              }
            >
              <Icon className="size-5" />
              {label}
            </NavLink>
          </Fragment>
        ))}
      </div>
      <div className="hidden flex-1 md:block" />
      {install && (
        <button type="button" onClick={install} className={cn(item, 'hidden h-12 md:flex')} aria-label="Install the POS as an app">
          <Download className="size-5" />
          Install
        </button>
      )}
      {stage !== 'demo' && <SyncStatus className={cn(item, 'h-12 md:flex')} />}
      {stage !== 'demo' && (
        <button type="button" onClick={lock} className={cn(item, 'h-12 md:flex')} aria-label="Lock the POS">
          <Lock className="size-5" />
          Lock
        </button>
      )}
      <button
        type="button"
        onClick={openCustomerDisplay}
        className={cn(item, 'hidden h-12 md:flex')}
        aria-label="Open the customer display"
        title="Opens a window to drag onto the customer's screen"
      >
        <MonitorSmartphone className="size-5" />
        Display
      </button>
      <button type="button" onClick={onShowPrinter} className={cn(item, 'hidden h-12 md:flex')} aria-label="Devices">
        <Printer className="size-5" />
        Devices
      </button>
      {stage !== 'demo' && (
        <button type="button" onClick={onShowLabels} className={cn(item, 'hidden h-12 md:flex')} aria-label="Print labels">
          <Tag className="size-5" />
          Labels
        </button>
      )}
      <button
        type="button"
        onClick={onShowShortcuts}
        className={cn(item, 'hidden h-12 md:flex')}
        aria-label="Keyboard shortcuts"
      >
        <Keyboard className="size-5" />
        Keys
      </button>
      <div
        title={`${register.cashier} · ${register.name}`}
        className="mx-auto mt-1 hidden size-9 shrink-0 place-items-center rounded-full bg-violet text-xs font-semibold text-white md:grid"
      >
        {initials(register.cashier)}
      </div>
    </nav>
  )
}
